import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import skuUtils from 'utils/Sku';

class OOSAlternateLinkBindings {
    /**
     * Track when user clicks "Select alternate" for an out of stock sample/reward
     * AC 1: Fire Adobe Analytics call with appropriate s.pagename based on item type
     * @param {object} item - The basket item
     */
    static trackSelectAlternateClick = item => {
        if (!item || !item.sku) {
            return;
        }

        const isSample = skuUtils.isSample(item.sku);
        const isBiReward = skuUtils.isBiReward(item.sku);

        let pageName;
        let pageDetail = '';

        // Determine the correct pageName based on item type
        if (isSample) {
            pageDetail = anaConsts.PAGE_DETAIL.SAMPLES;
            pageName = `basket:${anaConsts.PAGE_DETAIL.SAMPLES}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        } else if (isBiReward) {
            pageDetail = anaConsts.PAGE_DETAIL.REWARDS_BAZAAR;
            pageName = `basket:${anaConsts.PAGE_DETAIL.REWARDS_BAZAAR}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        }

        // Only fire analytics if we have a valid pageName
        if (pageName) {
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    pageName,
                    pageDetail,
                    actionInfo: anaConsts.ACTION_INFO.SELECT_ALTERNATE,
                    linkName: anaConsts.ACTION_INFO.SELECT_ALTERNATE,
                    internalCampaign: pageName
                }
            });
        }
    };

    /**
     * Track error message analytics for OOS alternate selection
     * @param {object} details - Error details
     * @param {string} details.fieldName - Field name ('smart sample', 'bi-reward', or 'gwp-reward')
     * @param {string} details.errorMessage - Error message to track
     */
    static trackErrorMessageAnalytics = details => {
        if (!details) {
            return;
        }

        const { fieldName, errorMessage } = details;

        let pageDetail, pageName;

        // Determine pageDetail and pageName based on the fieldName parameter
        if (fieldName === 'smart sample') {
            pageDetail = anaConsts.PAGE_DETAIL.SAMPLES;
            pageName = `basket:${anaConsts.PAGE_DETAIL.SAMPLES}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        } else if (fieldName === 'bi-reward') {
            pageDetail = anaConsts.PAGE_DETAIL.REWARDS_BAZAAR;
            pageName = `basket:${anaConsts.PAGE_DETAIL.REWARDS_BAZAAR}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        } else if (fieldName === 'gwp-reward') {
            pageDetail = anaConsts.PAGE_DETAIL.FREE_GIFT_MODAL;
            pageName = `basket:${anaConsts.PAGE_DETAIL.FREE_GIFT_MODAL}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        }

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName,
                pageDetail,
                fieldErrors: `sample oos: ${fieldName}`,
                errorMessages: errorMessage
            }
        });
    };

    /**
     * Track when the product samples modal opens for OOS item
     * @param {object} item - The item object (sample or BI reward)
     */
    static trackProductSamplesModalOpen = item => {
        const isSample = skuUtils.isSample(item);
        const isBiReward = skuUtils.isBiReward(item);

        let pageDetail, pageName;

        if (isSample) {
            pageDetail = anaConsts.PAGE_DETAIL.SAMPLES;
            pageName = `basket:${anaConsts.PAGE_DETAIL.SAMPLES}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        }

        if (isBiReward) {
            pageDetail = anaConsts.PAGE_DETAIL.BI_REWARDS;
            pageName = `basket:${anaConsts.PAGE_DETAIL.REWARDS_BAZAAR}:n/a:*${anaConsts.ACTION_INFO.SELECT_ALTERNATE}`;
        }

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName,
                pageDetail,
                linkName: pageName,
                internalCampaign: pageName
            }
        });
    };

    /**
     * Track when the Product Samples Modal is closed
     * @param {object} data - Analytics data
     * @param {string} data.productId - The main product ID
     * @param {number} data.samplesSelected - Number of samples selected
     * @param {boolean} data.hasOutOfStockAttempt - Whether user tried to select OOS samples
     */
    static trackProductSamplesModalClose = (data = {}) => {
        const { productId, samplesSelected, hasOutOfStockAttempt } = data;
        const pageType = anaConsts.PAGE_TYPES.PRODUCT;
        const pageDetail = 'product samples modal';
        const actionText = hasOutOfStockAttempt
            ? `close:${productId}:selected:${samplesSelected}:oos-attempt`
            : `close:${productId}:selected:${samplesSelected}`;
        const link = `${pageType}:${pageDetail}:${actionText}`;

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName: `${pageType}:${pageDetail}:${productId}:*`,
                pageType,
                pageDetail,
                linkName: link,
                internalCampaign: link
            }
        });
    };
}

export default OOSAlternateLinkBindings;
